/**
 * The freshness chip, translated.
 *
 * `FreshnessIndicator` lives in `@pazportal/ui`, which has no i18n provider —
 * it serves four apps with different bundles — so its state wording was English
 * constants. On a Spanish page the chip kept saying "Offline — Last Known Good",
 * which made the one surface carrying this platform's cardinal invariant the one
 * surface a Spanish-speaking customer could not read.
 *
 * This wrapper injects the translations once, so no call site has to remember
 * to. Every customer-portal usage goes through here; the staff apps keep the
 * English defaults, which is why the shared component's props are optional.
 */
import { FreshnessIndicator, type FreshnessIndicatorProps } from '@pazportal/ui';
import { useI18n } from '../i18n/I18nProvider';
import { formatDuration } from '../lib/formatters';

export type FreshnessProps = Omit<
  FreshnessIndicatorProps,
  'stateLabels' | 'shortStateLabels' | 'streamingLabel' | 'formatAge'
>;

export function Freshness(props: FreshnessProps) {
  const { t } = useI18n();
  return (
    <FreshnessIndicator
      {...props}
      stateLabels={{
        live: t('freshness.live'),
        stale: t('freshness.stale'),
        offline: t('freshness.offline'),
        unknown: t('freshness.unknown'),
      }}
      shortStateLabels={{
        live: t('freshness.live_short'),
        stale: t('freshness.stale_short'),
        offline: t('freshness.offline_short'),
        unknown: t('freshness.unknown_short'),
      }}
      streamingLabel={t('freshness.live_streaming')}
      formatAge={(iso) => {
        if (!iso) return t('freshness.relative_never');
        const ms = Date.now() - new Date(iso).getTime();
        if (Number.isNaN(ms)) return t('freshness.relative_never');
        // Under a minute reads as "just now" in both bundles; past that the
        // duration is a bare unit the sentence wraps, so "5h ago" and
        // "hace 5h" each come out in their own word order.
        if (ms < 60_000) return t('freshness.relative_just_now');
        return t('freshness.relative_ago', { age: formatDuration(ms) });
      }}
    />
  );
}
